<template>
  <div :style="style">
    <div
      class="relative rounded-xl border border-color bg-color min-h-210px select-none"
      :class="{ 'cursor-not-allowed opacity-50 pointer-events-none': disabled }"
      role="region"
      aria-label="Image upload area"
    >
      <div
        v-if="dataImageBase64"
        class="rounded-xl px-6 sm:px-10 pt-6 sm:pt-10 pb-6 space-y-4"
      >
        <img :src="dataImageBase64" class="w-full rounded-xl" alt="Uploaded Image" role="img" />
        <div class="flex">
          <a
            href="#"
            class="button-remove appearance-none inline-flex space-x-2 h-8 px-4 items-center justify-center rounded text-lg mx-auto font-bold"
            aria-label="Remove image"
            @click.prevent="onRemove"
            role="button"
          >
            <svg-trash-can aria-hidden="true" role="presentation"></svg-trash-can>
            <span>Remove</span>
          </a>
        </div>
      </div>
      <div
        v-if="!dataImageBase64"
        class="absolute inset-0 flex items-center justify-center"
        @dragover.prevent="onDragover"
        @drop.prevent="onDrop"
        role="button"
        tabindex="0"
        aria-label="Drag and drop upload area"
        @keydown.space.prevent="activateFileInput"
        @keydown.enter.prevent="activateFileInput"
      >
        <div class="text-center flex flex-col space-y-4">
          <svg-id class="mx-auto" aria-hidden="true" role="presentation"></svg-id>
          <div class="hidden lg:block">Drag and drop your files here or</div>
          <div class="lg:hidden">Take a photo or</div>
          <div class="flex flex-wrap justify-center gap-3">
            <a
              v-if="!isMobile"
              href="#"
              class="upload-action inline-flex h-8 px-4 items-center justify-center rounded text-lg font-medium"
              @click.prevent="$emit('on:mode', 'take-photo')"
              aria-label="Take a photo with camera"
              role="button"
            >
              Take a photo
            </a>
            <a
              href="#"
              class="upload-action inline-flex h-8 px-4 items-center justify-center rounded text-lg font-medium"
              @click.prevent="activateFileInput"
              aria-label="Choose file for upload"
              role="button"
              id="file-upload-trigger"
            >
              Choose file
            </a>
            <input
              id="file-upload"
              ref="fileUpload"
              class="sr-only"
              type="file"
              accept="image/*"
              @change="onChange"
              aria-label="Upload image file"
              aria-hidden="true"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SvgId from "@/components/Svg/SvgIdBig.vue";
import SvgTrashCan from "@/components/Svg/SvgTrashCan.vue";
import Compressor from "compressorjs";

export default {
  components: {
    SvgId,
    SvgTrashCan,
  },
  props: {
    value: [String, File, FileList],
    disabled: Boolean,
    isMobile: Boolean,
  },
  data() {
    return {
      dataImageBase64: null,
    };
  },
  computed: {
    style() {
      const borderColor =
        this.$store.state.config?.colors?.primaryOptionColor || "#51B44F";
      const backgroundColor =
        this.$store.state.config?.colors?.secondaryOptionColor || "#D4F3BB";
      return {
        "--border-color": borderColor,
        "--bg-color": `${backgroundColor}1A`,
      };
    },
  },
  methods: {
    handleFile(file) {
      new Compressor(file, {
        quality: 0.6,
        maxWidth: 600,
        convertTypes: ["image/jpeg"],
        success: (result) => {
          const reader = new FileReader();

          reader.onload = (e) => {
            // Trim the data:image/jpeg;base64
            const base64String = reader.result.split(",")[1];
            this.$emit("change", base64String);

            this.dataImageBase64 = e.target.result;
            this.$emit("input", this.dataImageBase64);
          };

          // Read the file
          reader.readAsDataURL(result);
        },
        error: (err) => {
          console.log(err.message);
        },
      });
    },
    onChange(event) {
      this.dataImageBase64 = event?.target?.files[0];

      if (!this.dataImageBase64) return;
      this.handleFile(this.dataImageBase64);
    },
    onRemove() {
      this.dataImageBase64 = null;
      this.$emit("input", null);
    },
    onDragover(event) {
      event.preventDefault();
    },
    onDrop(event) {
      this.dataImageBase64 = event?.dataTransfer?.files[0];

      if (!this.dataImageBase64) return;
      this.handleFile(this.dataImageBase64);
    },
    activateFileInput() {
      this.$refs.fileUpload.click();
    }
  },
  mounted() {
    if (this.value) {
      this.dataImageBase64 = this.value;
    }
  },
};
</script>

<style scoped>
.bg-color {
  background-color: inherit;
}
.min-h-210px {
  min-height: 210px;
}
.border-color {
  border-color: var(--border-color);
}
.bg-color {
  background-color: var(--bg-color);
}
.upload-action {
  background-color: #dadbdb;
  color: #333;
  height: 45px;
  transition: all 0.3s ease;
  border: 2px solid transparent;
  position: relative;
  overflow: hidden;
  text-decoration: none;
}
.upload-action:hover, .upload-action:focus {
  background-color: #aaaaaa;
  color: #000;
  border: 2px solid #666;
  box-shadow: 0 0 5px rgba(0,0,0,0.3);
  text-decoration: none;
  outline: none;
}
.upload-action:focus-visible {
  outline: 3px solid #51B44F;
  outline-offset: 2px;
}
.button-remove {
  background-color: #fb00001a;
  color: #ef1212;
  height: 36px;
  transition: all 0.3s ease;
  text-decoration: none;
}
.button-remove:hover, .button-remove:focus {
  background-color: #fb00003a;
  text-decoration: none;
  outline: none;
}
</style>